/* eslint-disable @stylistic/js/no-mixed-operators */

import { baseGlobals } from '@root/typescript/baseGlobals'

import { is } from '../is/is'
import { Number } from '../Number/Number'
import UserAgent from '../UserAgent/UserAgent'

import type {
  ITransformResponse,
  ITweenTransformParams
} from './definitions'

const psf = Number.parseFloat
const ua = UserAgent.getInstance()

/**
 * Animálás css transform.
 * @param params - Paraméterek.
 */
export default function transform (params: ITweenTransformParams): ITransformResponse {
  const { definitions, elem, onEnd } = params

  let hasTransX = false
  let fx: number
  let tx: number
  let dx: number
  let px: string
  let o = definitions.translateX

  if (o) {
    hasTransX = true
    fx = psf(o[0].toString())
    tx = psf(o[1].toString())
    dx = tx - fx
    px = 'px'
  }

  let hasTransY = false
  let fy: number
  let ty: number
  let dy: number
  let py: string
  o = definitions.translateY

  if (o) {
    hasTransY = true
    fy = psf(o[0].toString())
    ty = psf(o[1].toString())
    dy = ty - fy
    py = 'px'
  }

  let hasRotate = false
  let fr: number
  let tr: number
  let dr: number
  let pr: string
  o = definitions.rotate

  if (o) {
    hasRotate = true
    fr = psf(o[0].toString())
    tr = psf(o[1].toString())
    dr = tr - fr
    pr = 'deg'
  }

  let hasScale = false
  let fs: number
  let ts: number
  let ds: number
  o = definitions.scale

  if (o) {
    hasScale = true
    fs = psf(o[0].toString())
    ts = psf(o[1].toString())
    ds = ts - fs
  }

  // --- SCALE X

  let hasScaleX = false
  let fsX: number
  let tsX: number
  let dsX: number
  o = definitions.scaleX

  if (o) {
    hasScaleX = true
    fsX = psf(o[0].toString())
    tsX = psf(o[1].toString())
    dsX = tsX - fsX
  }

  // --- SCALE Y

  let hasScaleY = false
  let fsY: number
  let tsY: number
  let dsY: number
  o = definitions.scaleY

  if (o) {
    hasScaleY = true
    fsY = psf(o[0].toString())
    tsY = psf(o[1].toString())
    dsY = tsY - fsY
  }

  // --- OPACITY

  let hasOpacity = false
  let fa: number
  let ta: number
  let da: number
  o = definitions.opacity

  if (o) {
    hasOpacity = true
    fa = psf(o[0].toString())
    ta = psf(o[1].toString())
    da = ta - fa
  }

  let hasSkewX = false
  let fj: number
  let tj: number
  let dj: number
  let pj: string
  o = definitions.skewX

  if (o) {
    hasSkewX = true
    fj = psf(o[0].toString())
    tj = psf(o[1].toString())
    dj = tj - fj
    pj = 'px'
  }

  let hasSkewY = false
  let fb: number
  let tb: number
  let db: number
  let pb: string
  o = definitions.skewY

  if (o) {
    hasSkewY = true
    fb = psf(o[0].toString())
    tb = psf(o[1].toString())
    db = tb - fb
    pb = 'px'
  }

  const isClear = is.boolean(params.isClear) ? params.isClear : false

  /**
   * Ezt a függvényt fogja a rAF folyamatosan hívni, hogy beállítgassa az elemet.
   * @param value - Hol tart az animáció.
   */
  function progress (value: number): void {
    let t = ''

    t = t + (hasTransX ? `translateX(${ fx + dx * value + px }) ` : '')
    t = t + (hasTransY ? `translateY(${ fy + dy * value + py }) ` : '')
    t = t + (hasRotate ? `rotateZ(${ fr + dr * value + pr }) ` : '')
    t = t + (hasScale ? `scale(${ fs + ds * value }) ` : '')
    t = t + (hasScaleX ? `scaleX(${ fsX + dsX * value }) ` : '')
    t = t + (hasScaleY ? `scaleY(${ fsY + dsY * value }) ` : '')
    t = t + (hasSkewX ? `skewX(${ fj + dj * value + pj }) ` : '')
    t = t + (hasSkewY ? `skewY(${ fb + db * value + pb }) ` : '')

    if ((!is.empty(t) || hasOpacity) &&
      (value < 1 || !isClear)) {
      t = `${ t }translateZ(0) `
    }

    if (!is.empty(t)) {
      elem.style.transform = t
      // eslint-disable-next-line sonarjs/deprecation -- Legacy.
      elem.style.webkitTransform = t
    }

    if (hasOpacity) {
      elem.style.opacity = `${ fa + da * value }`
    }
  }

  const willChange = is.string(elem.style.willChange)

  /** Ezzel lehet leállítani egy futó animációt. */
  function cancel (): void {
    if (willChange) {
      elem.style.willChange = 'auto'
    }

    if (isClear) {
      elem.style.transform = ''
    }
  }

  /** Amikor véget ér az animáció. */
  function ready (): void {
    cancel()

    if (is.function(onEnd)) {
      onEnd(elem)
    }
  }

  // A will-change Chrome-ban okozhat problémákat, pl. a scale-nél... (pixelesedés).
  // Azért kell teszt esetében is, mert sok snapshot tesztünk már igy van megcsinálva.
  if (willChange && (ua.isFF || baseGlobals.isTest)) {
    elem.style.willChange = 'transform, opacity'
  }

  return {
    ready,
    progress,
    cancel
  }
}
